<script setup lang="ts">
import { computed } from 'vue'

import BaseImage from '@/components/atoms/BaseImage.vue'
import BetaStatusBadge from '@/components/beta-dashboard/atoms/BetaStatusBadge.vue'
import type { BetaPost } from '@/types/beta-dashboard'

const props = defineProps<{
    post: BetaPost
}>()

const emit = defineEmits<{
    edit: [post: BetaPost]
}>()

const status = computed(() => {
    if (props.post.sketch == 1) {
        return { label: 'Rascunho', tone: 'neutral' as const }
    }

    if (props.post.scheduled == 1) {
        return { label: 'Agendado', tone: 'warning' as const }
    }

    return { label: 'Publicado', tone: 'success' as const }
})

const formattedDate = computed(() => {
    const value = String(props.post.display_date ?? '').trim()
    if (!value) return 'Sem data definida'

    const dateParts = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    const date = dateParts
        ? new Date(Number(dateParts[1]), Number(dateParts[2]) - 1, Number(dateParts[3]))
        : new Date(value)

    if (Number.isNaN(date.getTime())) return 'Sem data definida'

    return new Intl.DateTimeFormat('pt-BR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    }).format(date)
})

const focalPoint = computed(() => {
    const value = props.post.mainImageExtra?.background_image_focal_point
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return { x: 50, y: 50 }
    }

    const point = value as Record<string, unknown>
    const x = Number(point.x)
    const y = Number(point.y)

    return {
        x: Number.isFinite(x) ? x : 50,
        y: Number.isFinite(y) ? y : 50,
    }
})
</script>

<template>
    <button
        type="button"
        class="beta-recent-post"
        :aria-label="`Editar ${post.title || 'publicação sem título'}`"
        @click="emit('edit', post)"
    >
        <span class="beta-recent-post__media">
            <BaseImage
                v-if="post.mainImage"
                :src="post.mainImage"
                :alt="post.title || 'Publicação sem título'"
                class-name="beta-recent-post__image"
                :style="{
                    objectPosition: `${focalPoint.x}% ${focalPoint.y}%`,
                }"
            />
            <span v-else class="beta-recent-post__fallback" aria-hidden="true">
                <span>Aa</span>
                <small>Sem imagem</small>
            </span>

            <BetaStatusBadge
                class="beta-recent-post__status"
                :label="status.label"
                :tone="status.tone"
            />
        </span>

        <span class="beta-recent-post__body">
            <span class="beta-recent-post__date">{{ formattedDate }}</span>
            <strong>{{ post.title || 'Publicação sem título' }}</strong>
            <span class="beta-recent-post__action">
                Editar publicação
                <span aria-hidden="true">→</span>
            </span>
        </span>
    </button>
</template>

<style scoped>
.beta-recent-post {
    display: grid;
    min-width: 0;
    overflow: hidden;
    border: 1px solid #dfe6ee;
    border-radius: 22px;
    background: #fff;
    text-align: left;
    transition: border-color 160ms ease, transform 160ms ease;
}

.beta-recent-post:hover,
.beta-recent-post:focus-visible {
    border-color: color-mix(in srgb, var(--main-color) 35%, #dfe6ee);
    transform: translateY(-2px);
}

.beta-recent-post:focus-visible {
    outline: 3px solid color-mix(in srgb, var(--main-color) 18%, transparent);
    outline-offset: 2px;
}

.beta-recent-post__media {
    position: relative;
    display: block;
    height: 164px;
    overflow: hidden;
    background: #edf1f6;
}

.beta-recent-post__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 220ms ease;
}

.beta-recent-post:hover .beta-recent-post__image {
    transform: scale(1.025);
}

.beta-recent-post__fallback {
    display: grid;
    width: 100%;
    height: 100%;
    place-content: center;
    justify-items: center;
    gap: 5px;
    background:
        radial-gradient(circle at 78% 22%, color-mix(in srgb, var(--main-color) 16%, transparent), transparent 32%),
        linear-gradient(145deg, #f6f8fb, #e9eef5);
    color: #8090a5;
}

.beta-recent-post__fallback span {
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 28px;
    font-weight: 650;
    letter-spacing: -0.06em;
}

.beta-recent-post__fallback small {
    font-size: 12px;
    font-weight: 750;
}

.beta-recent-post__status {
    position: absolute;
    top: 12px;
    left: 12px;
    font-size: 10px;
}

.beta-recent-post__body {
    display: grid;
    min-height: 134px;
    align-content: start;
    padding: 17px 18px 16px;
}

.beta-recent-post__date {
    color: #8a98aa;
    font-size: 12px;
    font-weight: 700;
    text-transform: capitalize;
}

.beta-recent-post__body > strong {
    display: -webkit-box;
    min-height: 42px;
    overflow: hidden;
    margin-top: 7px;
    color: #182a43;
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 16px;
    font-weight: 650;
    letter-spacing: -0.02em;
    line-height: 1.3;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}

.beta-recent-post__action {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 13px;
    color: var(--main-color);
    font-size: 12px;
    font-weight: 850;
}

.beta-recent-post__action span {
    font-size: 14px;
    transition: transform 160ms ease;
}

.beta-recent-post:hover .beta-recent-post__action span {
    transform: translateX(3px);
}

@media (max-width: 640px) {
    .beta-recent-post {
        grid-template-columns: 112px minmax(0, 1fr);
    }

    .beta-recent-post__media {
        height: 100%;
        min-height: 132px;
    }

    .beta-recent-post__status {
        top: 9px;
        left: 9px;
    }

    .beta-recent-post__body {
        min-height: 132px;
        padding: 15px;
    }
}
</style>
